import type { BalanceObservation, CoreAccount } from '../types/financial';

const AS_OF_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Local calendar date as `YYYY-MM-DD`. */
export function todayAsOfDate(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Prefer extract date when valid; otherwise today (local). */
export function resolveAsOfDate(raw: string | null | undefined, now = new Date()): string {
  const trimmed = raw?.trim().slice(0, 10);
  if (trimmed && AS_OF_DATE_RE.test(trimmed)) {
    const parsed = new Date(`${trimmed}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) return trimmed;
  }
  return todayAsOfDate(now);
}

/** Display like "Jun 15" (local calendar parse of YYYY-MM-DD). */
export function formatObservationDate(asOfDate: string): string {
  const trimmed = asOfDate.trim().slice(0, 10);
  const date = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Display like "Jun 15, 2026". */
export function formatObservationDateLong(asOfDate: string): string {
  const trimmed = asOfDate.trim().slice(0, 10);
  const date = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Upsert observations for the same accountId + asOfDate (re-upload same day replaces).
 * Sorted oldest → newest.
 */
export function upsertBalanceObservations(
  existing: BalanceObservation[],
  incoming: BalanceObservation[],
): BalanceObservation[] {
  if (incoming.length === 0) return existing;
  const keys = new Set(incoming.map((o) => `${o.accountId}|${o.asOfDate}`));
  const kept = existing.filter((o) => !keys.has(`${o.accountId}|${o.asOfDate}`));
  return [...kept, ...incoming].sort((a, b) => {
    const byDate = a.asOfDate.localeCompare(b.asOfDate);
    if (byDate !== 0) return byDate;
    return a.accountId.localeCompare(b.accountId);
  });
}

/**
 * Build point-in-time observations for imported investment accounts after merge.
 * Uses stable post-merge account ids (label match).
 */
export function observationsFromPortfolioImport(params: {
  mergedAccounts: CoreAccount[];
  importedAccounts: CoreAccount[];
  asOfDate: string;
}): BalanceObservation[] {
  const { mergedAccounts, importedAccounts, asOfDate } = params;
  const out: BalanceObservation[] = [];

  for (const incoming of importedAccounts) {
    const labelKey = incoming.label.trim().toLowerCase();
    const account = mergedAccounts.find(
      (a) =>
        a.kind === 'asset' &&
        a.isInvestment &&
        a.label.trim().toLowerCase() === labelKey,
    );
    if (!account) continue;
    out.push({
      accountId: account.id,
      asOfDate,
      balance: account.balance,
    });
  }

  return out;
}

/**
 * Baseline snapshot for investment accounts that have a balance but no history yet.
 * Recovers Trends after a race / import that wrote accounts without observations.
 */
export function seedMissingInvestmentObservations(
  accounts: CoreAccount[],
  observations: BalanceObservation[],
  asOfDate = todayAsOfDate(),
): BalanceObservation[] {
  const incoming: BalanceObservation[] = [];
  for (const account of accounts) {
    if (account.kind !== 'asset' || !account.isInvestment || account.balance <= 0) {
      continue;
    }
    const hasAny = observations.some((o) => o.accountId === account.id);
    if (hasAny) continue;
    incoming.push({
      accountId: account.id,
      asOfDate,
      balance: account.balance,
    });
  }
  return upsertBalanceObservations(observations, incoming);
}
